import getReplacement from './getReplacement';
import isObject from './isObject';
import {
	containsOnlyASingleWorkItem,
	containsWorkItem,
	getWorkItem
} from './workItem';

/**
 *
 * @param {String} input String like "[[address1]] where we are to replace the slug with the content"
 * @param {Object} eventData The data from the current event
 * @param {Object} mapping This is the whole mapping object for the current mapping configuration name
 * 	ex. window.DDC.WidgetData['ws-tagging'].mapping['critial_mass']
 * @param {Object} utils The current project/grouping utils.
 * @param {Object} lookups The current configuration lookups.
 * @param {Object} flags A feature flags object
 * @returns {String|Object|undefined} A string or object with the slugs replaced. undefined is returned for situations
 * where no slugs were resolved and no other text was in the input.
 * @example
 * '[[eventData.workItem]]' // if eventData.workItem doesn't exist this function will return undefined
 * '[[eventData.[[eventData.cool]]]]' // if eventData.cool exists but eventData.[eventData.cool's value] does not exist undefined is returned
 * '[[eventData.[[eventData.cool]]]]' // if eventData.cool does not exist I think it will return undefined as well
 */
const replaceSlugs = ({
	input,
	eventData,
	mapping,
	utils,
	lookups,
	flags,
	payload
}) => {
	let slugless = input;

	// The below is going through the string replacing slugs as it finds them
	// supports:
	// [[address1]] [[address2]]
	// [[form.confirm.[[eventData.trackingId]]]] : use eventData.trackingId as the property to find in form.confirm
	while (
		slugless &&
		typeof slugless === 'string' &&
		containsWorkItem(slugless)
	) {
		const workItem = getWorkItem(slugless);

		const replacement = getReplacement({
			str: workItem,
			eventData,
			mapping,
			utils,
			lookups,
			flags,
			payload
		});

		// this line is necessary as replaceSlugs can be used as a check for a logic rule
		// we want to return undefined so that we know nothing was found when trying to resolve a slug
		if (
			containsOnlyASingleWorkItem(slugless) &&
			typeof replacement === 'undefined'
		) {
			slugless = undefined;
		} else if (
			isObject(replacement) ||
			Array.isArray(replacement) ||
			typeof replacement === 'boolean'
		) {
			slugless = replacement;
		} else {
			slugless = slugless.replace(`[[${workItem}]]`, replacement || '');

			// This avoids converting numbers to strings.
			// ex. slugless: '24' then it should just be 24
			slugless =
				parseInt(slugless, 10) === replacement &&
				!/[^\d]/.test(slugless)
					? replacement
					: slugless;
		}
	}

	// we want to return undefined in situations where nothing was resolved from a slug lookup
	return typeof slugless === 'undefined' || typeof slugless !== 'string'
		? slugless
		: slugless.trim();
};

export default replaceSlugs;
